import {
  AbstractComparatorApiService,
  AddressApiSuccessResponse,
  AddressData,
  basicHomeIProduct,
  basicHomeProduct,
  basicHomeSupplier,
  Comparison,
  CustormerBenefitItem,
  getUspsBySellingPointsByOrderType,
  IComparatorApiServiceParameters,
  IGraphProvider,
  IProduct,
  OrderResult,
  OrderStatusResult,
  QuoteResponse,
} from '../../..';
import { FeaturesConnection, MasterAgreementStructure } from '../../../home';
import { LegacyOrderResponse } from '../../../../../helpers';
import { ApolloProvider } from '../apolloProvider';
import { gql } from '@apollo/client';
import { MoneysureQueryProvider } from './moneysureQueryProvider';
import {
  // calculateMoneysureQuery,
  recalculateMoneysureQuery,
} from '../../queries/moneysure/calculateMoneysureEventBased';
import {
  CalculateMoneysurePayload,
  PolicyDetails as MoneysurePolicyDetails,
} from './types';
import {
  HomeProduct as Product,
  ProductSupplier,
  ProductSupplierFeature,
  ProductsWithSuppliers,
} from '../../../graphProduct';
import { productsWithSuppliersQuery } from '../../queries';
import { CommonTechnical } from '../travel/types';

export class GraphMoneysureApi extends AbstractComparatorApiService {
  private client: any;
  private readonly queryProvider: MoneysureQueryProvider;
  private readonly graphProvider: IGraphProvider;

  constructor(params: IComparatorApiServiceParameters) {
    super(params);
    this.queryProvider = new MoneysureQueryProvider();
    this.graphProvider = new ApolloProvider(params);
  }

  setClient(client: any) {
    this.client = client;
  }

  getClient() {
    return this.client;
  }

  getComparison(): Promise<Comparison> {
    throw new Error('Method not implemented.');
  }

  getCustomerBenefits(): Promise<CustormerBenefitItem[]> {
    throw new Error('Method not implemented.');
  }

  getQuotePrefill(): Promise<QuoteResponse | false> {
    throw new Error('Method not implemented.');
  }

  validateAddress(): Promise<AddressApiSuccessResponse> {
    throw new Error('Method not implemented.');
  }

  getAddressData(): Promise<AddressData> {
    throw new Error('Method not implemented.');
  }

  getHomeFeaturesDetails(): Promise<FeaturesConnection> {
    throw new Error('Method not implemented.');
  }

  calculateHomeContentPackage(): Promise<MasterAgreementStructure> {
    throw new Error('Method not implemented.');
  }

  getProducts(searchCriteria: object): Promise<IProduct[]> {
    return new Promise<IProduct[]>((resolve, reject) => {
      const variables = this.queryProvider.validateMoneysureCalculateVariables(
        searchCriteria,
        this.credentials
      );

      let packages: MoneysurePolicyDetails[] = [];
      let products: Product[] = [];
      let suppliers: ProductSupplier[] = [];
      let commonTechnical: CommonTechnical[] = [];

      this.graphProvider
        .mutate<CalculateMoneysurePayload>(
          gql(recalculateMoneysureQuery),
          variables,
          'calculate',
          'network-only',
          `${process.env.KIZI_PUBLIC_DOMAIN}/event-based/graphql`
        )
        .then((data) => {
          if (
            data.comparatorCalculationResult &&
            data.comparatorCalculationResult.policyPolicyDetails
          ) {
            packages = data.comparatorCalculationResult.policyPolicyDetails;

            const result = data.comparatorCalculationResult;
            if (Array.isArray(result.commonTechnical)) {
              commonTechnical = result.commonTechnical as CommonTechnical[];
            }

            const productsWithSuppliersQueryVars =
              this.queryProvider.validateSuppliersVariables(result);

            this.graphProvider
              .query<ProductsWithSuppliers>(
                gql(productsWithSuppliersQuery),
                productsWithSuppliersQueryVars,
                'productsWithSuppliers',
                'network-only',
                `${process.env.KIZI_PUBLIC_DOMAIN}/product/graphql`
              )
              .then(async (productsWithSuppliers) => {
                products = productsWithSuppliers.products;
                suppliers = productsWithSuppliers.suppliers;

                const formatted = this.formatMoneysureProduct(
                  this.credentials,
                  packages,
                  products,
                  suppliers,
                  commonTechnical
                );
                resolve(formatted);
              });
          }
        })
        .catch((error) => {
          console.log(error);
          reject('No products returned');
        });
    });
  }

  recalculatePremium(searchCriteria: object): Promise<number> {
    const variables = this.queryProvider.validateMoneysureRecalculateVariables(
      searchCriteria,
      this.credentials
    );

    return new Promise<number>((resolve) => {
      this.graphProvider
        .mutate<CalculateMoneysurePayload>(
          gql(recalculateMoneysureQuery),
          variables,
          'calculate',
          'network-only',
          `${process.env.KIZI_PUBLIC_DOMAIN}/event-based/graphql`
        )
        .then((res) => {
          resolve(
            res.comparatorCalculationResult.policyPolicyDetails[0]
              ?.totalInstallmentAmount
          );
        });
    });
  }

  formatMoneysureProduct(
    credentials: any,
    packages: MoneysurePolicyDetails[],
    products: Product[],
    suppliers: ProductSupplier[],
    commonTechnical: CommonTechnical[]
  ): IProduct[] {
    return packages.map((policyDetails) => {
      const {
        productCode,
        customProperties,
        // moneySurePremiumResponses,
        totalInstallmentAmount,
      } = policyDetails;

      const product: Product = {
        ...basicHomeProduct,
        ...products.find((p) => p.krn === productCode),
      };

      const supplier: ProductSupplier = {
        ...basicHomeSupplier,
        ...suppliers.find((s) => s.krn === customProperties.supplierKrn),
      };

      const {
        insuranceCardUrl,
        orderType,
        productType,
        termsAndConditionsUrl,
        customAttributes,
        name: productName,
        eventInformation,
      } = product;

      const {
        about,
        advantages,
        disadvantages,
        features,
        howItWorks,
        name,
        nameLogoUrl,
        sellingPoints,
        sustainabilityScore,
        taskDivisions,
      } = supplier;

      return {
        ...basicHomeIProduct,
        commonTechnical: [
          {
            senderId: credentials.clientId,
            sessionId: '',
            transactionReference: commonTechnical[0].transactionReference || '',
            accessCode: commonTechnical[0].accessCode || '',
          },
        ],
        bigProductFeature: {
          ...basicHomeIProduct.bigProductFeature,
        },
        characteristics: this.buildCharacteristics(features || []),
        customAttributes,
        id: `${productCode}`,
        image: nameLogoUrl,
        orderType,
        priceInfo: {
          ...basicHomeIProduct.priceInfo,
          initial: totalInstallmentAmount,
          // moneySurePremiumResponses[0].moneySurePremiums[0]
          //   .monthlyGrossPremium,
          total: totalInstallmentAmount,
          // moneySurePremiumResponses[0].moneySurePremiums[0]
          //   .monthlyGrossPremium,
        },
        productCode: productCode || '',
        productType: productType || '',
        prosAndCons: {
          cons: {
            ...basicHomeIProduct.prosAndCons.cons,
            title1: disadvantages?.[0]?.description || '',
            title2: disadvantages?.[1]?.description || '',
          },
          pros: {
            ...basicHomeIProduct.prosAndCons.cons,
            title1: advantages?.[0]?.description || '',
            title2: advantages?.[1]?.description || '',
          },
        },
        provider: {
          ...basicHomeIProduct.provider,
          description: about,
          howItWorks,
          insuranceCardUrl,
          label: name,
          name,
          sellingPoints,
          sustainabilityScore: sustainabilityScore || '',
          taskDivisions,
          termsAndConditionsUrl,
          id: supplier.krn,
          logo: nameLogoUrl,
        },
        purchasableProduct: orderType !== 'NOT_ORDERABLE',
        usps: getUspsBySellingPointsByOrderType(sellingPoints, orderType),
        type: 'moneysure',
        subscriptionName: productName || '',
        eventInformation,
      };
    });
  }

  async orderAffiliateContentPackage(): Promise<OrderResult> {
    throw new Error('Method not implemented.');
  }

  sendEmailToCustomer(): Promise<string> {
    throw new Error('Method not implemented.');
  }

  getCalculationInput(
    transactionReference: string
  ): Promise<MasterAgreementStructure> {
    console.log('getCalculationInput', transactionReference);
    throw new Error('Method not implemented.');
  }

  orderStatus(orderId: string): Promise<OrderStatusResult> {
    console.log('orderStatus', orderId);
    throw new Error('Method not implemented.');
  }

  getOrderDefinition(
    product: IProduct,
    productType: 'cars' | 'energy' | 'energyAuction'
  ): Promise<LegacyOrderResponse['data']> {
    console.log('getOrderDefinition', product, productType);
    throw new Error('Method not implemented.');
  }

  checkMembershipStatus(): Promise<CustormerBenefitItem[]> {
    throw new Error('Method not implemented.');
  }

  private buildCharacteristics = (features: ProductSupplierFeature[]) =>
    features
      ? features.map((feature) => {
        return {
          label: feature.name,
          value: feature.value,
        };
      })
      : [];
}
